/**
 * CAR-IA · Vehicle Feed (W01 v3.1) — buyer discovery surface.
 * Rebuilt on the v3 component library (TopBar, ColorRail, VehicleCard).
 * Data is sample/seed today; the GET /vehicles feed endpoint lands in the
 * T0 loop-closing increment (Epic A) — wire `useVehicleFeedV3()` here then.
 */
import React from 'react';
import {
  TopBar,
  BUYER_NAV,
  ColorRail,
  VehicleCard,
  TasadorChip,
  DecisionChip,
  PriceDropChip,
} from '../../../commodity/components/caria';
import type { TrustMark } from '../../../commodity/components/caria';
import { useAuthChrome } from '../../../core/identity-access/hooks/useAuthChrome';

interface FeedVehicle {
  id: string;
  title: string;
  subtitle: string;
  priceUsd: string;
  priceSecondary: string;
  imageUrl: string;
  photoCount: number;
  premium?: boolean;
  favorite?: boolean;
  year: string;
  mileageKm: string;
  fuel: string;
  location: string;
  distanceKm: string;
  trustSignals: string;
  marks: TrustMark[];
  priceChips: React.ReactNode;
}

const SEED: FeedVehicle[] = [
  {
    id: 'mazda-cx5-2018',
    title: 'Mazda CX-5 Grand Touring',
    subtitle: '2018 · 2.5 Skyactiv AWD',
    priceUsd: '$23,650',
    priceSecondary: 'RD$ 1,420,000',
    imageUrl: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=800&h=600&fit=crop&q=80',
    photoCount: 31,
    premium: true,
    favorite: true,
    year: '2018',
    mileageKm: '76,200',
    fuel: 'Gas',
    location: 'Piantini',
    distanceKm: '3.2km',
    trustSignals: '4/4',
    marks: [
      { key: 'tasado', label: 'Tasado', on: true },
      { key: 'inspected', label: 'Inspected', on: true },
      { key: 'carfax', label: 'Carfax', on: true },
      { key: 'verificado', label: 'Verificado', on: true },
    ],
    priceChips: (
      <>
        <TasadorChip price="$25,170" />
        <DecisionChip verdict="good-deal" label="Excelente · 6% bajo" />
        <PriceDropChip amount="RD$50k" days={12} />
      </>
    ),
  },
  {
    id: 'honda-civic-2020',
    title: 'Honda Civic EX-L',
    subtitle: '2020 · 1.5 Turbo',
    priceUsd: '$19,900',
    priceSecondary: 'RD$ 1,194,000',
    imageUrl:
      'https://images.unsplash.com/photo-1605559424843-9e4c228bf1c2?w=800&h=600&fit=crop&q=80',
    photoCount: 18,
    year: '2020',
    mileageKm: '41,500',
    fuel: 'Gas',
    location: 'Naco',
    distanceKm: '5.1km',
    trustSignals: '3/4',
    marks: [
      { key: 'tasado', label: 'Tasado', on: true },
      { key: 'inspected', label: 'Inspected', on: false },
      { key: 'carfax', label: 'Carfax', on: true },
      { key: 'verificado', label: 'Verificado', on: true },
    ],
    priceChips: <DecisionChip verdict="fair-deal" />,
  },
  {
    id: 'toyota-rav4-2019',
    title: 'Toyota RAV4 XLE',
    subtitle: '2019 · 2.5 AWD',
    priceUsd: '$24,300',
    priceSecondary: 'RD$ 1,458,000',
    imageUrl:
      'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800&h=600&fit=crop&q=80',
    photoCount: 24,
    year: '2019',
    mileageKm: '58,900',
    fuel: 'Gas',
    location: 'Bella Vista',
    distanceKm: '7.4km',
    trustSignals: '2/4',
    marks: [
      { key: 'tasado', label: 'Tasado', on: true },
      { key: 'inspected', label: 'Inspected', on: false },
      { key: 'carfax', label: 'Carfax', on: false },
      { key: 'verificado', label: 'Verificado', on: true },
    ],
    priceChips: <DecisionChip verdict="overpriced" label="3% sobre mercado" />,
  },
];

export const VehicleFeedV3: React.FC = () => {
  const authChrome = useAuthChrome();

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surf-page)' }}>
      <TopBar {...authChrome} nav={BUYER_NAV} />
      <ColorRail />
      <main className="cia-feed">
        {SEED.map((v) => (
          <VehicleCard
            key={v.id}
            title={v.title}
            subtitle={v.subtitle}
            priceUsd={v.priceUsd}
            priceSecondary={v.priceSecondary}
            imageUrl={v.imageUrl}
            photoCount={v.photoCount}
            premium={v.premium}
            favorite={v.favorite}
            priceChips={v.priceChips}
            spec={{
              year: v.year,
              mileageKm: v.mileageKm,
              fuel: v.fuel,
              location: v.location,
              distanceKm: v.distanceKm,
            }}
            trustMarks={v.marks}
            trustSignals={v.trustSignals}
            onContact={() => {}}
            onMessage={() => {}}
          />
        ))}
      </main>
    </div>
  );
};

export default VehicleFeedV3;
